import React, { useEffect, useState, useCallback, createRef, ReactNode, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { observer } from "mobx-react-lite";
import {
  DataGrid as MUIDataGrid,
  GridColDef,
  GridEventListener,
  GridRenderCellParams,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
  GridFilterModel,
  GridValueGetterParams,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  getGridStringOperators,
  getGridNumericOperators,
  getGridDateOperators,
  getGridSingleSelectOperators,
  ValueOptions,
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColumnHeaderParams,
  GridHeaderCheckbox,
  GridPaginationModel,
  GridSingleSelectColDef,
  GridCallbackDetails,
} from "@mui/x-data-grid";
import { Box, IconButton, Pagination, TablePagination, ThemeProvider } from "@mui/material";
import Button from "@mui/material/Button";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import TablePaginationActions from "@mui/material/TablePagination/TablePaginationActions";

import "./DataGrid.scss";
import { Column, ColumnType, IDataGrid } from "./interfaces";
import DataGridError from "./DataGridError";
import DataGridTotalResults from "./DataGridTotalResults";
import DataGridSelectAllMenu, { DataGridSelectAllMenuRef } from "./DataGridSelectAllMenu";
import DataGridCell from "./DataGridCell";
import getLocalizationMapping from "./DataGridLocalizationMapping";
import { translationService } from "../../services/translation/TranslationService";
import { MUIComponentsTheme } from "../../services/theme-service/ThemeService";
import { formatNumberBasedOnLanguage } from "../../helpers/MathHelpers";
import dateService from "../../services/date-service/DateService";
import LoadingSpinner from "../loading-spinner/LoadingSpinner";
import Tooltip from "../tooltip/Tooltip";

const DataGrid = observer((props: IDataGrid) => {
  const {
    rowClickPath,
    conditionForRowClickAvailable,
    hasDynamicRowClickPath,
    itemTypeKey,
    dataGridStorage,
    insideDialog = false,
    singleSelection = false,
    noResultsMessageKey = "",
    noSelection = false,
    withTopSpacing = false,
    toolbarCustomButton = null,
    filterModelChangeCallback = null,
    hasDefaultColumnVisibility = false,
    isOnSide = false,
    isCollapsible = false,
    isStatusDropdownTable = false,
    customSelectAll = false,
    customLocalizationMappings,
  } = { ...props };

  const CustomDataGridToolbar: React.FunctionComponent = () => (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      {toolbarCustomButton && (
        <Button
          sx={{'& .MuiButton-startIcon': {marginRight: 0}}}
          startIcon={<toolbarCustomButton.icon />}
          onClick={toolbarCustomButton.callback}
          disabled={toolbarCustomButton.isDisabled}
        >
          {translationService.translate(toolbarCustomButton.textKey)}
        </Button>
      )}
    </GridToolbarContainer>
  );

  const history = useHistory();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortModel, setSortModel] = useState<GridSortModel>(
    dataGridStorage.defaultSort ? [dataGridStorage.defaultSort] : []
  );
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [paginationModel, setPaginationModel] = useState({
    page: page,
    pageSize: pageSize,
  });

  const tableItemsTotalCount = dataGridStorage.clientSidePagination
    ? dataGridStorage.data?.length
    : dataGridStorage.data?.totalCount;
  const [tableItemsTotalCountState, setTableItemsTotalCountState] = useState(
    tableItemsTotalCount || 0
  );

  const numberOfPages = useMemo(
    () => Math.ceil(tableItemsTotalCountState / pageSize),
    [tableItemsTotalCountState, pageSize]
  );

  const handleSortModelChange = useCallback(
    async (newSortModel: GridSortModel) => {
      if (!newSortModel[0]) return;

      setSortModel(newSortModel);

      if (!dataGridStorage.clientSidePagination) {
        if (page === 0) {
          await dataGridStorage?.sortItemsBy(newSortModel[0]);
          await dataGridStorage.load();
        } else {
          setPage(0);
          await dataGridStorage?.sortItemsBy(newSortModel[0]);
        }
      }
    },
    [dataGridStorage, page]
  );

  const handleFilterModelChange = useCallback(
    async (newFilterModel: GridFilterModel, details: GridCallbackDetails<"filter">) => {
      if (dataGridStorage.clientSidePagination) {
        await dataGridStorage.setGridFilter(newFilterModel, page !== 0);
      } else {
        await dataGridStorage.setGridFilterAndLoad(newFilterModel, page !== 0);
      }
      filterModelChangeCallback && filterModelChangeCallback();
    },
    [dataGridStorage, filterModelChangeCallback, page]
  );

  const handleStateChange = useCallback(
    (e: any) => {
      if (dataGridStorage.clientSidePagination) {
        const currentVisibleItemsCount = Object.values(e.visibleRowsLookup).filter(
          (value) => value === true
        ).length;
        if (tableItemsTotalCountState !== currentVisibleItemsCount) {
          setTableItemsTotalCountState(currentVisibleItemsCount);
          setPaginationModel({ page: 0, pageSize: pageSize });
        }
      }
    },
    [dataGridStorage.clientSidePagination, pageSize, tableItemsTotalCountState]
  );

  const handleOnPageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);

      if (dataGridStorage.clientSidePagination) {
        setPaginationModel({ page: newPage, pageSize: pageSize });
      }
    },
    [dataGridStorage.clientSidePagination, pageSize]
  );

  const handleOnPageSizeChange = useCallback(
    (newPageSize: number) => {
      setPageSize(newPageSize);
      setPage(0);

      if (dataGridStorage.clientSidePagination) {
        setPaginationModel({ page: 0, pageSize: newPageSize });
      }
    },
    [dataGridStorage.clientSidePagination]
  );

  const handlePaginationModelChange = useCallback((model: GridPaginationModel) => {
    setPaginationModel(model);
  }, []);

  const getDataGridClassName = () => {
    let className: string = "";
    if (singleSelection) className = className.concat("selectable-grid-row ");
    if (rowClickPath || hasDynamicRowClickPath) className = className.concat("clickable-grid-row ");

    return className;
  };

  const getComponentProps = useCallback(() => {
    if (!isOnSide || !dataGridStorage.useGridFiltering) return {};

    return {
      panel: {
        placement: "bottom-end",
      },
    };
  }, [dataGridStorage.useGridFiltering, isOnSide]);

  const translatedValueOptions = useCallback(
    (column: Column): ValueOptions[] =>
      column.valueOptions!.map((o) => ({
        label: translationService.translate(o.label),
        value: o.value,
      })),
    []
  );

  const getValue = useCallback((column: Column, params: GridValueGetterParams): any => {
    const displayValues = params.row.displayValues;
    return column.type === ColumnType.DateTime
      ? new Date(displayValues[column.key]?.value)
      : column.type === ColumnType.SingleSelect
      ? displayValues[column.key]?.value.toString()
      : displayValues[column.key]?.toDisplay;
  }, []);

  const renderCell = useCallback((column: Column, params: GridRenderCellParams<any>): ReactNode => {
    return (
      <DataGridCell item={params.row} column={column.key} cellIsClickable={column.containsLink} isProgressCell={column.showsProgress}/>
    );
  }, []);

  const setNonSingleSelectFilterOperators = useCallback((column: GridColDef, type: ColumnType) => {
    switch (type) {
      case ColumnType.String:
        column.filterOperators = getGridStringOperators().filter(
          (o) =>
            o.value === "equals" ||
            o.value === "contains" ||
            o.value === "isEmpty" ||
            o.value === "isNotEmpty"
        );
        break;
      case ColumnType.Number:
        column.filterOperators = getGridNumericOperators()
          .filter(
            (o) =>
              o.value === ">" ||
              o.value === "<" ||
              o.value === ">=" ||
              o.value === "<=" ||
              o.value === "=" ||
              o.value === "!=" ||
              o.value === "isEmpty" ||
              o.value === "isNotEmpty"
          )
          .map((o) => {
            o.getValueAsString = (value) => formatNumberBasedOnLanguage(value);
            return o;
          });
        break;
      case ColumnType.DateTime:
        column.filterOperators = getGridDateOperators()
          .filter(
            (o) =>
              o.value === "is" ||
              o.value === "not" ||
              o.value === "after" ||
              o.value === "before" ||
              o.value === "isEmpty" ||
              o.value === "isNotEmpty"
          )
          .map((o) => {
            o.getValueAsString = (value) => dateService.formatDate(value);
            return o;
          });
        break;
    }
  }, []);

  const setSingleSelectFilterOperators = useCallback(
    (data: Column, column: GridSingleSelectColDef) => {
      column.filterOperators = getGridSingleSelectOperators()
        .filter((o) => o.value === "is" || o.value === "not")
        .map((o) => {
          o.getValueAsString = (value) => {
            var tk = data.valueOptions?.find((v) => v.value === value)?.label;
            return tk ? translationService.translate(tk) : `${value}`;
          };
          return o;
        });
    },
    []
  );

  const getColumnForNonSingleSelect = useCallback(
    (c: Column) => {
      const column: GridColDef = {
        field: c.key,
        headerClassName: "data-grid-headers",
        flex: c.flex ?? 1,
        headerName: translationService.translate(c.name ?? ""),
        sortable: c.sortable,
        filterable: c.filterable ?? false,
        type: c.type,
        align: "left",
        headerAlign: "left",
        valueGetter: (p) => (dataGridStorage.clientSidePagination ? getValue(c, p) : undefined)
      };

      column.renderCell = (params: GridRenderCellParams<any>) => renderCell(c, params);

      setNonSingleSelectFilterOperators(column, c.type ?? ColumnType.String);

      return column;
    },
    [dataGridStorage.clientSidePagination, getValue, renderCell, setNonSingleSelectFilterOperators]
  );

  const getColumnForSingleSelect = useCallback(
    (c: Column) => {
      const column: GridSingleSelectColDef = {
        field: c.key,
        headerClassName: "data-grid-headers",
        flex: c.flex ?? 1,
        headerName: translationService.translate(c.name ?? ""),
        sortable: c.sortable,
        filterable: c.filterable ?? false,
        align: "left",
        headerAlign: "left",
        type: "singleSelect",
        valueOptions: () => translatedValueOptions(c),
        valueGetter: (p) => (dataGridStorage.clientSidePagination ? getValue(c, p) : undefined),
        getOptionValue: (value: any) => value?.value,
        getOptionLabel: (value: any) => translationService.translate(value?.label),
      };

      column.renderCell = (params: GridRenderCellParams<any>) => renderCell(c, params);

      setSingleSelectFilterOperators(c, column);

      return column;
    },
    [
      dataGridStorage.clientSidePagination,
      getValue,
      renderCell,
      setSingleSelectFilterOperators,
      translatedValueOptions,
    ]
  );

  const getColumns = useCallback(
    () =>
      dataGridStorage.columns.map((x: Column) => {
        if (x.type !== ColumnType.SingleSelect) {
          return getColumnForNonSingleSelect(x);
        } else {
          return getColumnForSingleSelect(x);
        }
      }),
    [dataGridStorage.columns, getColumnForNonSingleSelect, getColumnForSingleSelect]
  );

  const selectAllMenuRef = createRef<DataGridSelectAllMenuRef>();

  const selectPageItems = useCallback(() => {
    const pageItemsIds = dataGridStorage.data?.items?.map((i: any) => i.id) ?? [];
    const newSelectItemsIds = [...selectionModel, ...pageItemsIds].filter(
      (v, i, array) => array.indexOf(v) === i
    );

    setSelectionModel(newSelectItemsIds);
    dataGridStorage.setSelectedItems(newSelectItemsIds.map((i: any) => String(i)));
  }, [dataGridStorage, selectionModel]);

  const selectAllItems = useCallback(async () => {
    const newSelectItemsIds = await dataGridStorage.loadOnlyIds();

    setSelectionModel(newSelectItemsIds);
    dataGridStorage.setSelectedItems(newSelectItemsIds.map((i: any) => String(i)));
  }, [dataGridStorage]);

  const deselectAllItems = useCallback(() => {
    setSelectionModel([]);
    dataGridStorage.setSelectedItems([]);
  }, [dataGridStorage]);

  const handleRowClick: GridEventListener<"rowClick"> = (params: GridRowParams) => {
    if (rowClickPath && !singleSelection) {
      if (
        conditionForRowClickAvailable &&
        !dataGridStorage.data.items.find((i: any) => i.id === params.id)[
          conditionForRowClickAvailable
        ]
      ) {
        return;
      }

      const rowLink = rowClickPath.replace(":id", String(params.id));
      history.push(rowLink);
      return;
    }
    if (hasDynamicRowClickPath) {
      const rowLink = params.row.path!.replace(":id", String(params.id));
      history.push(rowLink);
      return;
    }
  };

  useEffect(() => {
    async function loadItemsSetForTableStorage() {
      let start = page * pageSize;
      let end = start + pageSize;
      await dataGridStorage?.loadItemsSet(start, end);
    }
    if (dataGridStorage.clientSidePagination) return;

    loadItemsSetForTableStorage();
  }, [page, pageSize, dataGridStorage]);

  useEffect(() => {
    setTableItemsTotalCountState((prevRowCountState: any) =>
      tableItemsTotalCount !== undefined ? tableItemsTotalCount : prevRowCountState
    );
  }, [tableItemsTotalCount, setTableItemsTotalCountState]);

  useEffect(() => {
    if (dataGridStorage.shouldResetSelection) {
      setSelectionModel([]);
      dataGridStorage.resetSelectionState();
    }
  }, [dataGridStorage, dataGridStorage.shouldResetSelection]);

  useEffect(() => {
    if (dataGridStorage.shouldResetPagination) {
      dataGridStorage.resetPaginationState();
      setPage(0);

      if (dataGridStorage.clientSidePagination) {
        setPaginationModel({ page: 0, pageSize: pageSize });
      }
    }
  }, [dataGridStorage, dataGridStorage.shouldResetPagination, pageSize]);

  if (dataGridStorage.error) return <DataGridError error={dataGridStorage.error} />;

  const localeText = useMemo(
    () => getLocalizationMapping(itemTypeKey, noResultsMessageKey, customLocalizationMappings),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemTypeKey, noResultsMessageKey, customLocalizationMappings, translationService.language]
  );

  const CustomDataGridPagination: React.FunctionComponent = () => {
    const customActionsComponents: React.FunctionComponent = () => (
      <Box>
        <Tooltip title="general.go-to-first-page">
          <span>
            <IconButton
              onClick={() => {
                handleOnPageChange(0);
              }}
              disabled={page === 0}
            >
              <FirstPageIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="general.go-to-previous-page">
          <span>
            <IconButton onClick={() => handleOnPageChange(page - 1)} disabled={page === 0}>
              <KeyboardArrowLeft />
            </IconButton>
          </span>
        </Tooltip>
        <Pagination
          color="primary"
          count={numberOfPages}
          page={page + 1}
          hideNextButton
          hidePrevButton
          onChange={(_, value) => handleOnPageChange(value - 1)}
        />
        <Tooltip title="general.go-to-next-page">
          <span>
            <IconButton
              onClick={() => handleOnPageChange(page + 1)}
              disabled={page >= numberOfPages - 1}
            >
              <KeyboardArrowRight />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="general.go-to-last-page">
          <span>
            <IconButton
              onClick={() => handleOnPageChange(numberOfPages - 1)}
              disabled={page >= numberOfPages - 1}
            >
              <LastPageIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );

    return (
      <TablePagination
        component="div"
        count={tableItemsTotalCountState}
        page={page}
        onPageChange={(_, value) => handleOnPageChange(value)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={(event) => handleOnPageSizeChange(Number(event.target.value))}
        rowsPerPageOptions={[20, 40, 80, 100]}
        showFirstButton={!isOnSide}
        showLastButton={!isOnSide}
        ActionsComponent={!isOnSide ? customActionsComponents : TablePaginationActions}
        labelRowsPerPage={localeText.MuiTablePagination.labelRowsPerPage}
        labelDisplayedRows={(props) => localeText.MuiTablePagination.labelDisplayedRows?.(props)}
      />
    );
  };

  return (
    <>
      <div
        className={`data-grid 
        ${withTopSpacing ? "top-spacing" : ""} 
        ${!insideDialog ? "fixed-height" : ""} 
        ${isStatusDropdownTable ? "grid-status-dropdown-table" : ""}
        ${dataGridStorage.isCollapsed ? "collapsed" : ""}`}
      >
        {dataGridStorage.noPagination && (
          <DataGridTotalResults
            itemTypeKey={itemTypeKey}
            totalResults={tableItemsTotalCountState}
            selectedItemsCount={selectionModel?.length}
            insideDialog={insideDialog}
            singleSelection={singleSelection}
            isCollapsible={isCollapsible}
            handleCollapse={dataGridStorage.handleCollapse}
          />
        )}
        {hasDefaultColumnVisibility && !dataGridStorage.defaultColumnVisibility ? (
          <LoadingSpinner />
        ) : (
          <ThemeProvider theme={MUIComponentsTheme}>
            <MUIDataGrid
              autoHeight={insideDialog}
              rows={dataGridStorage.rows}
              className={getDataGridClassName()}
              columns={
                customSelectAll
                  ? [
                      {
                        ...GRID_CHECKBOX_SELECTION_COL_DEF,
                        renderHeader: (params: GridColumnHeaderParams) => (
                          <>
                            <GridHeaderCheckbox {...params} />
                            <DataGridSelectAllMenu
                              ref={selectAllMenuRef}
                              selectPage={selectPageItems}
                              selectAll={selectAllItems}
                              deselectAll={deselectAllItems}
                            />
                          </>
                        ),
                      },
                      ...getColumns(),
                    ]
                  : getColumns()
              }
              initialState={{
                sorting: {
                  sortModel: dataGridStorage.defaultSort ? [dataGridStorage.defaultSort] : [],
                },
                columns: {
                  columnVisibilityModel: dataGridStorage.defaultColumnVisibility,
                },
              }}
              onColumnVisibilityModelChange={(newVisibilityModel) => {
                dataGridStorage.setDefaultVisibilityModel(newVisibilityModel);
              }}
              columnVisibilityModel={dataGridStorage.defaultColumnVisibility}
              slotProps={getComponentProps()}
              columnHeaderHeight={isStatusDropdownTable ? 50 : 35}
              rowHeight={isStatusDropdownTable ? 25 : 35}
              checkboxSelection={customSelectAll ? true : !singleSelection && !noSelection}
              disableColumnMenu
              rowCount={tableItemsTotalCountState}
              pagination
              hideFooter={dataGridStorage.noPagination}
              disableRowSelectionOnClick={!singleSelection || noSelection}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={[20, 40, 80, 100]}
              paginationMode={dataGridStorage.clientSidePagination ? "client" : "server"}
              sortingOrder={["asc", "desc"]}
              sortingMode={dataGridStorage.clientSidePagination ? "client" : "server"}
              filterMode={dataGridStorage.clientSidePagination ? "client" : "server"}
              onRowClick={handleRowClick}
              keepNonExistentRowsSelected
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              onFilterModelChange={handleFilterModelChange}
              filterModel={dataGridStorage.filterData.gridFilter}
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={(newSelectionModel: GridRowSelectionModel) => {
                setSelectionModel(newSelectionModel);
                dataGridStorage.setSelectedItems(newSelectionModel.map((x) => x?.toString()));
              }}
              loading={dataGridStorage.isLoading}
              slots={{
                toolbar: dataGridStorage.useGridFiltering ? CustomDataGridToolbar : null,
                pagination: CustomDataGridPagination,
              }}
              localeText={localeText}
              onColumnHeaderClick={(params, e) => {
                if (!customSelectAll) return;

                if (params.field === GRID_CHECKBOX_SELECTION_FIELD) {
                  e.preventDefault();
                  selectAllMenuRef.current?.openMenu(e.currentTarget);
                }
              }}
              onStateChange={handleStateChange}
            />
          </ThemeProvider>
        )}
      </div>
    </>
  );
});

export default DataGrid;
